import { PostgrestClient } from "@supabase/postgrest-js";
import type { Database, DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import {
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../utils/uuid";

export type InsightKeyFieldWithData = Database["public"]["Views"]["insight_key_fields"]["Row"];

/** Write operations go to the table. */
export class SupabaseInsightKeyFieldService extends SupabaseService<"insight_key_field"> {
  table = "insight_key_field" as const;
}

/** Read operations go to the view, which includes `has_data`. */
export class SupabaseInsightKeyFieldsViewService extends SupabaseServiceView<"insight_key_fields"> {
  table = "insight_key_fields" as const;
}

/** Fetch up to 5 distinct non-null sample values for a field from employee text data. */
export async function getFieldPreviewValues(
  client: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  fieldKey: string,
): Promise<string[]> {
  const { data, error } = await client
    .from("person_text_data")
    .select("value")
    .eq("account_id", account_id)
    .eq("key", fieldKey)
    .not("value", "is", null)
    .limit(100);
  if (error || !data) return [];
  return [...new Set(data.map((r) => r.value))].slice(0, 5);
}

export async function getInsightKeyFieldMetrics(
  client: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  fieldKey: string,
): Promise<
  | Pick<
      Database["public"]["Views"]["insight_key_field_metrics"]["Row"],
      "coverage" | "cardinality"
    >
  | undefined
> {
  const { data, error } = await client
    .from("insight_key_field_metrics")
    .select("coverage, cardinality")
    .eq("account_id", account_id)
    .eq("field_key", fieldKey)
    .maybeSingle();

  if (error) throw error;
  return data ?? undefined;
}
